import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
  UsersIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { StudentPageLayout } from '../../components/student/StudentPageLayout';
import { StudentSection } from '../../components/student/StudentSection';

interface GroupCourse {
  id: string;
  title: string;
  description: string;
  subject: string;
  duration: number;
  courseType: string;
  maxStudents: number;
  minStudents: number;
  groupPricePerHour: number;
  finalPricePerHour: number;
  groupDescription?: string;
  teacher: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
    };
    rating: number;
  };
  activeGroups: Array<{
    groupBookingId: string;
    currentStudents: number;
    maxStudents: number;
    date: string;
    startTime: string;
    endTime: string;
    canJoin: boolean;
  }>;
}

const subjects = ['数学', '物理', '化学', '英语', '计算机', '艺术', '音乐', '体育', '语文', '历史', '地理'];

const GroupCourses: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [courses, setCourses] = useState<GroupCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [joinLoading, setJoinLoading] = useState<string | null>(null);

  // 计算每个学生的平均价格
  const calculateAveragePrice = (course: GroupCourse, studentCount: number) => {
    if (studentCount === 0) return 0;
    
    // 确保课程数据有效
    const pricePerHour = course.groupPricePerHour || course.finalPricePerHour || 0;
    const duration = course.duration || 1;
    
    // 检查数值有效性
    if (isNaN(pricePerHour) || pricePerHour <= 0) return 0;
    if (isNaN(duration) || duration <= 0) return 0;
    
    const totalPrice = pricePerHour * duration;
    return Math.round(totalPrice / studentCount * 100) / 100;
  };

  // 生成价格预览
  const generatePriceBreakdown = (course: GroupCourse) => {
    const breakdown = [];
    
    // 确保课程数据有效
    const minStudents = course.minStudents || 2;
    const maxStudents = course.maxStudents || 6;
    
    if (isNaN(minStudents) || isNaN(maxStudents) || minStudents <= 0 || maxStudents <= 0) {
      return [];
    }
    
    for (let i = minStudents; i <= maxStudents; i++) {
      breakdown.push({
        studentCount: i,
        averagePrice: calculateAveragePrice(course, i),
        isMinimum: i === minStudents,
        isMaximum: i === maxStudents
      });
    }
    return breakdown;
  };

  useEffect(() => {
    fetchGroupCourses();
  }, [subjectFilter]);

  const fetchGroupCourses = async () => {
    try {
      setLoading(true);
      setError('');
      
      // 检查用户是否已登录
      if (!token) {
        setError('请先登录才能查看群组课程');
        return;
      }

      const params = new URLSearchParams();
      if (subjectFilter) params.append('subject', subjectFilter);

      const response = await fetch(`/api/bookings/group-courses?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('登录已过期，请重新登录');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '获取群组课程失败');
      }

      const data = await response.json();
      setCourses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('获取群组课程失败:', error);
      setError(error instanceof Error ? error.message : '获取群组课程失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (groupBookingId: string) => {
    try {
      setJoinLoading(groupBookingId);
      
      // 检查用户是否已登录
      if (!token) {
        alert('请先登录才能加入群组课程');
        return;
      }
      
      const response = await fetch(`/api/bookings/join-group/${groupBookingId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert('登录已过期，请重新登录');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '加入群组失败');
      }

      const data = await response.json();
      
      // 显示成功消息
      alert('成功加入群组课程！');
      
      // 刷新数据
      fetchGroupCourses();
      
      // 跳转到我的预约页面
      navigate('/student/bookings');
    } catch (error) {
      console.error('加入群组失败:', error);
      alert(error instanceof Error ? error.message : '加入群组失败');
    } finally {
      setJoinLoading(null);
    }
  };

  const handleCreateBooking = async (courseId: string) => {
    navigate(`/booking/${courseId}?type=group`);
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.teacher.user.firstName.includes(searchTerm) ||
    course.teacher.user.lastName.includes(searchTerm)
  );

  const summary = useMemo(() => {
    const totalCourses = courses.length
    const totalGroups = courses.reduce(
      (sum, course) => sum + (course.activeGroups?.length || 0),
      0
    )
    const openSeats = courses.reduce((sum, course) => {
      const seats = (course.activeGroups || []).reduce(
        (acc, group) => acc + Math.max(group.maxStudents - group.currentStudents, 0),
        0
      )
      return sum + seats
    }, 0)
    const minPrice = courses.reduce<number | null>((min, course) => {
      const minStudents = Math.max(course.minStudents || 1, 1)
      const price = calculateAveragePrice(course, minStudents)
      if (!price) return min
      if (min === null) return price
      return Math.min(min, price)
    }, null)

    return { totalCourses, totalGroups, openSeats, minPrice }
  }, [courses])

  const metrics = useMemo(() => [
    {
      label: '可选课程',
      value: summary.totalCourses || '—',
      helper: '实时同步'
    },
    {
      label: '活跃群组',
      value: summary.totalGroups || '0',
      helper: '可立即加入'
    },
    {
      label: '剩余席位',
      value: summary.openSeats || '0',
      helper: '按时间段统计'
    },
    {
      label: '起始人均价',
      value: summary.minPrice ? `¥${summary.minPrice.toFixed(0)}` : '—',
      helper: '最低开班人数'
    }
  ], [summary])

  const heroExtra = (
    <div className="space-y-4 text-white">
      <p className="text-sm text-white/80">
        AI 会根据你的学习偏好和时间自动锁定最优群组，人数达标后系统会即时提醒。
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
          <p className="text-xs uppercase tracking-[0.3em] text-white/70">开放群组</p>
          <p className="text-2xl font-semibold">{summary.totalGroups}</p>
          <p className="text-xs text-white/70 mt-1">支持即刻报名</p>
        </div>
        <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
          <p className="text-xs uppercase tracking-[0.3em] text-white/70">剩余席位</p>
          <p className="text-2xl font-semibold">{summary.openSeats}</p>
          <p className="text-xs text-white/70 mt-1">按时间段实时刷新</p>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <StudentPageLayout
        title="群组课程"
        description="与志同道合的伙伴一起上课，达到最小人数即可开班，AI 会帮你计算最划算的组合。"
        metrics={metrics}
        heroExtra={heroExtra}
        actions={
          <>
            <button
              onClick={fetchGroupCourses}
              className="inline-flex items-center px-4 py-2 rounded-full bg-white/15 text-white text-sm hover:bg-white/25"
            >
              刷新列表
            </button>
            <button
              onClick={() => navigate('/student/bookings')}
              className="inline-flex items-center px-4 py-2 rounded-full border border-white/40 text-white text-sm hover:bg-white/10"
            >
              查看我的预约
            </button>
          </>
        }
      >
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </StudentPageLayout>
    )
  }

  return (
    <StudentPageLayout
      title="群组课程"
      description="与志同道合的同学组队上课，AI 会实时计算每个时间段的人均成本，达到最小人数即可开班。"
      metrics={metrics}
      heroExtra={heroExtra}
      actions={
        <>
          <button
            onClick={fetchGroupCourses}
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/15 text-white text-sm hover:bg-white/25 transition"
          >
            刷新列表
          </button>
          <button
            onClick={() => navigate('/student/bookings')}
            className="inline-flex items-center px-4 py-2 rounded-full border border-white/40 text-white text-sm hover:bg-white/10 transition"
          >
            查看我的预约
          </button>
        </>
      }
      contentWidth="wide"
    >
      <StudentSection
        title="群组课程玩法"
        description="用最划算的价格锁定优质老师，达到最小人数即可自动确认。"
        icon={InformationCircleIcon}
      >
        <div className="grid gap-4 text-sm text-slate-600 md:grid-cols-2">
          <div className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
            <div>
              <p className="font-medium text-slate-900">价格更友好</p>
              <p className="text-slate-500 mt-1">人数越多，人均价格越低，系统会实时计算最优组合。</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-purple-500 mt-1.5" />
            <div>
              <p className="font-medium text-slate-900">互动学习</p>
              <p className="text-slate-500 mt-1">与其他同学结伴上课，形成稳定的学习伙伴。</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" />
            <div>
              <p className="font-medium text-slate-900">自动确认</p>
              <p className="text-slate-500 mt-1">达到最小人数立即确认，无需等待老师人工审核。</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full bg-pink-500 mt-1.5" />
            <div>
              <p className="font-medium text-slate-900">可以发起新群</p>
              <p className="text-slate-500 mt-1">找不到合适时间？一键创建新的时间段邀请同学加入。</p>
            </div>
          </div>
        </div>
      </StudentSection>

      <StudentSection title="搜索与筛选" description="锁定适合的科目、老师与时间段">
        <div className="flex flex-col gap-4 md:flex-row">
          <input
            type="text"
            placeholder="搜索课程、老师..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">所有科目</option>
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>
      </StudentSection>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl">
          {error}
        </div>
      )}

      {filteredCourses.length === 0 ? (
        <StudentSection title="暂无符合条件的群组课程" description="试试切换科目或调整时间">
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂未找到合适课程</h3>
            <p className="text-gray-500">当前筛选条件下没有可加入的群组，换个条件再试试。</p>
          </div>
        </StudentSection>
      ) : (
        <StudentSection
          title={`开放群组 (${filteredCourses.length})`}
          description="达到最小人数即可开课，AI 会提醒你剩余席位。"
          className="space-y-6"
        >
          {filteredCourses.map((course) => (
            <div key={course.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* 课程信息 */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                    <p className="text-gray-600 mb-3">{course.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          {course.subject}
                        </span>
                      </span>
                      <span className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {course.duration}小时
                      </span>
                      <span className="flex items-center">
                        <UsersIcon className="h-4 w-4 mr-1" />
                        {course.minStudents}-{course.maxStudents}人
                      </span>
                      <span className="flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                        ¥{course.groupPricePerHour}/小时
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">
                      ¥{calculateAveragePrice(course, course.maxStudents).toFixed(2)} - ¥{calculateAveragePrice(course, course.minStudents).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">每人价格区间</div>
                    <div className="text-xs text-green-600 font-medium">
                      最多{course.maxStudents}人时最便宜
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      老师：{course.teacher.user.firstName} {course.teacher.user.lastName}
                    </div>
                  </div>
                </div>

                {course.groupDescription && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-gray-700">{course.groupDescription}</p>
                  </div>
                )}

                {/* 价格预览表 */}
                <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-2 text-purple-600" />
                    💰 人数越多越优惠
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                         {generatePriceBreakdown(course).map((item, index) => (
                       <div key={index} className="text-center bg-white rounded-lg p-3 border">
                         <div className="text-lg font-bold text-purple-600">
                           ¥{item.averagePrice.toFixed(2)}
                         </div>
                         <div className="text-xs text-gray-600">
                           {item.studentCount}人时/每人
                         </div>
                         {item.isMinimum && (
                           <div className="text-xs text-green-600 font-medium mt-1">
                             ✓ 最少人数
                           </div>
                         )}
                         {item.isMaximum && (
                           <div className="text-xs text-blue-600 font-medium mt-1">
                             🎯 最优价格
                           </div>
                         )}
                       </div>
                     ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    📢 提示：人数达到最少要求即可开课，人数越多每人费用越低
                  </p>
                </div>
              </div>

              {/* 现有群组 */}
              {course.activeGroups && course.activeGroups.length > 0 ? (
                <div className="p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">可加入的群组</h4>
                  <div className="space-y-3">
                    {course.activeGroups.map((group) => (
                      <div key={group.groupBookingId} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {new Date(group.date).toLocaleDateString('zh-CN')}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {group.startTime} - {group.endTime}
                          </div>
                          <div className="flex items-center text-sm">
                            <UsersIcon className="h-4 w-4 mr-1" />
                            <span className={group.currentStudents >= course.minStudents ? 'text-green-600 font-medium' : 'text-yellow-600'}>
                              {group.currentStudents}/{group.maxStudents}人
                            </span>
                            {group.currentStudents >= course.minStudents && (
                              <CheckCircleIcon className="h-4 w-4 ml-1 text-green-600" />
                            )}
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600">
                              ¥{((course.groupPricePerHour * course.duration) / Math.max(group.currentStudents + 1, course.minStudents)).toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              加入后每人支付
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          {group.currentStudents >= course.minStudents ? (
                            <span className="text-sm text-green-600 font-medium">已确认开课</span>
                          ) : (
                            <span className="text-sm text-yellow-600">等待{course.minStudents - group.currentStudents}人</span>
                          )}
                          
                          {group.canJoin ? (
                            <button
                              onClick={() => handleJoinGroup(group.groupBookingId)}
                              disabled={joinLoading === group.groupBookingId}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                            >
                              {joinLoading === group.groupBookingId ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  加入中...
                                </>
                              ) : (
                                <>
                                  <UserPlusIcon className="h-4 w-4 mr-1" />
                                  加入群组
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-500 bg-gray-200 px-3 py-2 rounded-lg">
                              群组已满
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-yellow-500 mb-2" />
                  <p className="text-gray-600 mb-4">暂无可加入的群组，您可以创建新的时间段</p>
                  <button
                    onClick={() => handleCreateBooking(course.id)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    创建新群组
                  </button>
                </div>
              )}
            </div>
          ))}
        </StudentSection>
      )}
    </StudentPageLayout>
  );
};

export default GroupCourses; 